// CheckoutHandler.js
import shopifyFetch from "./APIFetch.js";

const CART_ID_KEY = "shopify_cart_id";
const CUSTOMER_TOKEN_KEY = "customerAccessToken";

const getCartId = () => localStorage.getItem(CART_ID_KEY);
const getCustomerToken = () => sessionStorage.getItem(CUSTOMER_TOKEN_KEY);

function normalizeCartId(raw) {
    if (!raw) return null;
    if (/^https?:\/\//i.test(raw)) return { type: "url", value: raw }; // someone stored checkoutUrl
    // keep ?key if present — Shopify accepts it in IDs
    if (!raw.startsWith("gid://shopify/Cart/")) {
        throw new Error(`Invalid cart identifier: ${raw}`);
    }
    return { type: "gid", value: raw };
}

/* ——— GraphQL ——— */
const Q_CART = `
  query GetCart($id: ID!) {
    cart(id: $id) { id checkoutUrl }
  }
`;

const Q_CUSTOMER = `
  query GetCustomer($token: String!) {
    customer(customerAccessToken: $token) {
      id
      email
      phone
      firstName
      lastName
      defaultAddress {
        firstName
        lastName
        address1
        address2
        city
        province
        provinceCode
        zip
        country
        countryCodeV2
        phone
      }
    }
  }
`;

const M_CART_BUYER_UPDATE = `
  mutation CartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
    cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
      cart { id }
      userErrors { field message }
    }
  }
`;

const M_CART_DELIVERY_ADD = `
  mutation CartDeliveryAddressesAdd($id: ID!, $addresses: [CartSelectableAddressInput!]!) {
    cartDeliveryAddressesAdd(cartId: $id, addresses: $addresses) {
      cart { id }
      userErrors { field message }
      warnings { code message target }
    }
  }
`;

// Legacy fallback (older Storefront API)
const M_LEGACY_BUYER_WITH_PREFS = `
  mutation LegacyCartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
    cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
      cart { id }
      userErrors { field message }
    }
  }
`;

// --- GraphQL bits ---
const M_CART_CREATE = `
  mutation cartCreate($input: CartInput) {
    cartCreate(input: $input) {
      cart { id checkoutUrl }
      userErrors { field message }
    }
  }
`;

const M_CART_DISCOUNT_CODES_UPDATE = `
  mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
    cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
      cart {
        id
        discountCodes { code applicable }
        cost {
          subtotalAmount { amount currencyCode }
          totalAmount { amount currencyCode }
        }
        lines(first: 50) {
          edges {
            node {
              id
              quantity
              cost { totalAmount { amount currencyCode } }
              discountAllocations {
                discountedAmount { amount currencyCode }
              }
            }
          }
        }
      }
      userErrors { field message }
    }
  }
`;

function clearCartId() { localStorage.removeItem("cartId"); }
// If you store full IDs like "...?key=abcd", keep this guard to nuke stale ones.
function looksLikeFullCartId(id){ return typeof id === "string" && id.includes("?key="); }

// Your Storefront fetch wrapper (already in your project):
// async function shopifyFetch(query, variables) { ... }

/**
 * Apply a discount code to the current cart (Storefront API only).
 * Creates a cart if missing. Returns updated cart, userErrors, and applicability flag.
 *
 * @param {string} discountCode
 * @returns {Promise<{
 *   applicable: boolean,
 *   cart: any,
 *   userErrors: Array<{field?: string[], message: string}>
 * }>}
 */
export async function applyDiscountCode(discountCode) {
    const code = (discountCode || "").trim();
    if (!code) throw new Error("applyDiscountCode: missing code");

    // Ensure we have a valid cart id (Shopify now wants the full id with ?key=)
    let cartId = getCartId();
    if (cartId && !looksLikeFullCartId(cartId)) {
        console.warn("Stale cart id without ?key= detected — clearing and recreating.");
        clearCartId();
        cartId = null;
    }

    // Create an empty cart if needed
    if (!cartId) {
        const created = await shopifyFetch(M_CART_CREATE, { input: {} });
        const createErrors = created?.cartCreate?.userErrors || [];
        if (createErrors.length) {
            throw new Error("cartCreate failed: " + createErrors.map(e => e.message).join("; "));
        }
        const newCart = created?.cartCreate?.cart;
        if (!newCart?.id) throw new Error("cartCreate did not return an id");
        cartId = newCart.id;
        setCartId(cartId);
    }

    // Apply/replace discount codes (Shopify accepts an array; typically only 1 code is allowed)
    const result = await shopifyFetch(M_CART_DISCOUNT_CODES_UPDATE, {
        cartId,
        discountCodes: [code],
    });

    const payload = result?.cartDiscountCodesUpdate;
    const userErrors = payload?.userErrors || [];
    const cart = payload?.cart || null;

    // "applicable" is per-code; if Shopify deems it ineligible it’ll be false.
    // Note: product-specific codes may be applicable=true but only discount matching lines.
    const applicable = !!cart?.discountCodes?.[0]?.applicable;

    return { applicable, cart, userErrors };
}


/**
 * Generate a Checkout URL for the existing cart.
 * - Optionally applies discount code(s) beforehand.
 * - If customerAccessToken exists, associates cart + pre-fills contact/address.
 *
 * @param {Object} [opts]
 * @param {string} [opts.discountCode]              // single code
 * @param {string[]} [opts.discountCodes]           // multiple codes; overrides discountCode
 * @returns {Promise<{url: string, cartId: string|null, appliedDiscounts?: Array<{code:string, applicable:boolean}>}>}
 */
export const generateCheckoutLink = async ({ discountCode, discountCodes } = {}) => {
    const stored = getCartId();
    if (!stored) throw new Error("Missing cartId in localStorage.");

    const norm = normalizeCartId(stored);
    if (norm.type === "url") return { url: norm.value, cartId: null };
    const cartId = norm.value;

    const token = getCustomerToken();
    let customer = null;

    if (token) {
        // 1) Load customer profile
        try {
            const r = await shopifyFetch(Q_CUSTOMER, { token });
            customer = r?.data?.customer ?? r?.customer ?? null;
        } catch (e) {
            console.warn("GetCustomer failed:", e);
        }

        // 2) Associate customer to cart
        try {
            const res = await shopifyFetch(M_CART_BUYER_UPDATE, {
                cartId,
                buyerIdentity: { customerAccessToken: token },
            });
            const errs = res?.data?.cartBuyerIdentityUpdate?.userErrors;
            if (errs?.length) console.warn("Buyer update errors:", errs);
        } catch (e) {
            console.warn("cartBuyerIdentityUpdate failed:", e);
        }
    }

    // 2.5) Apply discount codes if provided
    const codes = Array.isArray(discountCodes)
        ? discountCodes.filter(Boolean)
        : (discountCode ? [discountCode] : []);
    let appliedDiscounts;

    if (codes.length) {
        try {
            const res = await shopifyFetch(M_CART_DISCOUNT_CODES_UPDATE, {
                cartId,
                discountCodes: codes,
            });
            const errs = res?.data?.cartDiscountCodesUpdate?.userErrors;
            if (errs?.length) console.warn("cartDiscountCodesUpdate errors:", errs);
            appliedDiscounts = res?.data?.cartDiscountCodesUpdate?.cart?.discountCodes ?? undefined;
        } catch (e) {
            console.warn("cartDiscountCodesUpdate failed:", e);
        }
    }

    // 3) Prefill checkout from customer default address (if any)
    if (customer) {
        const email = customer.email || null;
        const phone = customer.phone || null;
        const a = customer.defaultAddress || null;

        // 3a) contact + delivery preference
        try {
            const buyerIdentity = {
                email,
                phone,
                countryCode: a?.countryCodeV2 || "PL",
                preferences: { delivery: { deliveryMethod: ["SHIPPING"] } },
            };
            const res = await shopifyFetch(M_CART_BUYER_UPDATE, { cartId, buyerIdentity });
            const errs = res?.data?.cartBuyerIdentityUpdate?.userErrors;
            if (errs?.length) console.warn("Buyer prefill (contact) errors:", errs);
        } catch (e) {
            console.warn("Prefill buyer failed:", e);
        }

        // 3b) address (new API; correct var names)
        if (a) {
            const addressInputNew = {
                selected: true,
                address: {
                    deliveryAddress: {
                        firstName: a.firstName || customer.firstName || null,
                        lastName: a.lastName || customer.lastName || null,
                        address1: a.address1 || null,
                        address2: a.address2 || null,
                        city: a.city || null,
                        provinceCode: a.provinceCode || null,
                        zip: a.zip || null,
                        countryCode: a.countryCodeV2 || "PL",
                        phone: a.phone || phone || null,
                    },
                },
            };

            let delivered = false;
            try {
                const res = await shopifyFetch(M_CART_DELIVERY_ADD, {
                    id: cartId,
                    addresses: [addressInputNew],
                });
                const errs = res?.data?.cartDeliveryAddressesAdd?.userErrors;
                if (errs?.length) {
                    console.warn("cartDeliveryAddressesAdd errors:", errs);
                } else {
                    delivered = true;
                }
            } catch (e) {
                console.warn("cartDeliveryAddressesAdd failed. Falling back.", e);
            }

            // Legacy fallback if your Storefront API version is old
            if (!delivered) {
                try {
                    const legacyBuyerIdentity = {
                        email,
                        phone,
                        countryCode: a.countryCodeV2 || "PL",
                        deliveryAddressPreferences: [
                            { deliveryAddress: {
                                    firstName: a.firstName || customer.firstName || null,
                                    lastName: a.lastName || customer.lastName || null,
                                    address1: a.address1 || null,
                                    address2: a.address2 || null,
                                    city: a.city || null,
                                    provinceCode: a.provinceCode || null,
                                    zip: a.zip || null,
                                    countryCode: a.countryCodeV2 || "PL",
                                    phone: a.phone || phone || null,
                                } },
                        ],
                    };
                    const res = await shopifyFetch(M_LEGACY_BUYER_WITH_PREFS, { cartId, buyerIdentity: legacyBuyerIdentity });
                    const errs = res?.data?.cartBuyerIdentityUpdate?.userErrors;
                    if (errs?.length) console.warn("Legacy buyer prefs errors:", errs);
                } catch (e) {
                    console.warn("Legacy deliveryAddressPreferences fallback failed:", e);
                }
            }
        }
    }

    // 4) Checkout URL
    const result = await shopifyFetch(Q_CART, { id: cartId });
    const cart = result?.data?.cart ?? result?.cart;
    const url = cart?.checkoutUrl;
    if (!url) throw new Error("checkoutUrl not found for provided cartId.");

    return { url, cartId: cart.id || cartId, appliedDiscounts };
};
